import { useState, useEffect, useContext } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Loader2, 
  AlertCircle, 
  CheckCircle,
  Lock,
  ArrowLeft
} from 'lucide-react';
import Footer from '../components/Footer';
import {
  getDobRetryMessage,
  isDobRequiredError,
  PARTICIPATION_INTENT_TYPES,
  saveParticipationDobIntent,
} from '../lib/participationDob';
import { isPrimaryHostJoinIneligible } from '../utils/gameParticipation';

/**
 * JoinGame page - handles invite link joins
 * URL: /join/{game_id}
 * 
 * This page:
 * 1. Shows public-safe run preview details
 * 2. Validates user can join (age, blocks, capacity)
 * 3. Joins user to the game
 */
const JoinGame = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [alreadyInGame, setAlreadyInGame] = useState(false);
  const [primaryHostJoinBlocked, setPrimaryHostJoinBlocked] = useState(false);

  useEffect(() => {
    if (!location.state?.dobCompletionSuccess) return;
    toast.success(getDobRetryMessage(location.state.participationIntentType));
    navigate(`/join/${gameId}`, { replace: true, state: null });
  }, [gameId, location.state, navigate]);

  const requestParticipationDob = () => {
    const saved = saveParticipationDobIntent({
      type: PARTICIPATION_INTENT_TYPES.DIRECT_JOIN,
      returnTo: `/join/${gameId}`,
    });
    if (!saved) {
      toast.error('Unable to open date of birth entry. Please try again.');
      return false;
    }
    navigate('/complete-dob');
    return true;
  };
  
  useEffect(() => {
    fetchGame();
  }, [gameId, token]);
  
  const fetchGame = async () => {
    if (!gameId) {
      setError('Invalid game link');
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get(`/runs/${gameId}/preview`);
      const gameData = response.data;
      setGame(gameData);
      
      // Check if user is already in the game
      if (token && user) {
        try {
          const eligibility = await apiClient.get(`/runs/${gameId}/join-eligibility`);
          if (eligibility.data?.reason_code === 'already_joined') {
            setAlreadyInGame(true);
            setJoined(true);
          } else if (isPrimaryHostJoinIneligible(eligibility.data)) {
            setPrimaryHostJoinBlocked(true);
          }
        } catch {
          // Keep the public preview usable even if eligibility cannot be checked.
        }
      }
    } catch (err) {
      console.error('Failed to fetch game:', err);
      if (err.response?.status === 404) {
        setError('Game not found');
      } else if (err.response?.status === 403) {
        setError('You cannot access this game');
      } else {
        setError('Failed to load game details');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleJoin = async () => {
    if (!token) {
      // Store the intended destination and redirect to login
      sessionStorage.setItem('redirectAfterLogin', `/join/${gameId}`);
      navigate('/', { state: { showLogin: true, redirectTo: `/join/${gameId}` } });
      return;
    }

    if (!user?.date_of_birth) {
      requestParticipationDob();
      return;
    }
    
    setJoining(true);
    try {
      // Try to join team_a first (the endpoint auto-balances)
      await apiClient.post(`/games/${gameId}/join/team_a`);
      setJoined(true);
      toast.success('You joined the game!');
      
      // Redirect to game details after short delay
      setTimeout(() => {
        navigate(`/games/${gameId}`);
      }, 1500);
    } catch (err) {
      if (isDobRequiredError(err)) {
        requestParticipationDob();
        return;
      }
      console.error('Failed to join game:', err);
      const detail = err.response?.data?.detail;
      
      if (detail?.includes('full')) {
        toast.error('This game is full');
      } else if (detail?.includes('age')) {
        toast.error("You don't meet the age requirements for this game");
      } else if (detail?.includes('cannot join')) {
        toast.error('You cannot join this game');
      } else if (err.response?.status === 403) {
        toast.error('You cannot join this game');
      } else {
        toast.error(detail || 'Failed to join game');
      }
    } finally {
      setJoining(false);
    }
  };
  
  const formatDateTime = (dateStr, tz) => {
    if (!dateStr) return { date: 'TBD', time: '' };
    // Backend stores times in UTC without Z suffix - add it for proper parsing
    const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    const timezone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric',
        timeZone: timezone 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        timeZone: timezone 
      })
    };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-zinc-600">Loading game...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-zinc-900 mb-2">Unable to Join</h1>
            <p className="text-zinc-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </Card>
        </div>
        <Footer variant="slim" />
      </div>
    );
  }
  
  const { date, time } = formatDateTime(game?.date_time, game?.tz);
  const playerCount = game?.player_count || 0;
  const isFull = Boolean(game?.is_full) || playerCount >= (game?.max_players || 10);
  const isCancelled = game?.status === 'cancelled';
  const isCompleted = game?.status === 'completed';
  const runNoun = game?.public_type === 'training' ? 'Training' : 'Run';
  const title = game?.public_title || game?.title || runNoun;
  const locationLabel = game?.public_location_label || game?.city || 'Location shared after joining';
  
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-1">
              {title}
            </h1>
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-600 text-xs font-medium rounded-full">
              <Lock className="w-3 h-3" />
              Public {runNoun} Preview
            </span>
          </div>
          
          {/* Game Details */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-zinc-700">
              <Calendar className="w-5 h-5 text-zinc-400 flex-shrink-0" />
              <div>
                <p className="font-medium">{date}</p>
                <p className="text-sm text-zinc-500">{time}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-zinc-700">
              <MapPin className="w-5 h-5 text-zinc-400 flex-shrink-0" />
              <div>
                <p className="font-medium">{locationLabel}</p>
                <p className="text-sm text-zinc-500">{game?.city}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-zinc-700">
              <Users className="w-5 h-5 text-zinc-400 flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {playerCount} / {game?.max_players} players
                </p>
                <p className="text-sm text-zinc-500">
                  Skill: {game?.skill_level_min} - {game?.skill_level_max}
                </p>
              </div>
            </div>
          </div>
          
          {/* Action Button */}
          {joined ? (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">
                  {alreadyInGame ? "You're already in this game!" : "You've joined!"}
                </span>
              </div>
              <Button 
                onClick={() => navigate(`/games/${gameId}`)}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                View Game Details
              </Button>
            </div>
          ) : primaryHostJoinBlocked ? (
            <div className="text-center">
              <p className="text-zinc-600 font-medium mb-4">
                The primary host cannot join this {runNoun.toLowerCase()} as a player.
              </p>
              <Button
                onClick={() => navigate(`/games/${gameId}`)}
                variant="outline"
                className="w-full"
              >
                View {runNoun} Details
              </Button>
            </div>
          ) : isCancelled ? (
            <div className="text-center">
              <p className="text-red-600 font-medium mb-4">This {runNoun.toLowerCase()} has been cancelled</p>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Browse Other Games
              </Button>
            </div>
          ) : isCompleted ? (
            <div className="text-center">
              <p className="text-zinc-600 font-medium mb-4">This {runNoun.toLowerCase()} has already ended</p>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Browse Other Games
              </Button>
            </div>
          ) : isFull ? (
            <div className="text-center">
              <p className="text-amber-600 font-medium mb-4">This {runNoun.toLowerCase()} is full</p>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full">
                Browse Other Games
              </Button>
            </div>
          ) : (
            <Button 
              onClick={handleJoin}
              disabled={joining}
              className="w-full bg-orange-500 hover:bg-orange-600 h-12 text-base"
              data-testid="join-game-btn"
            >
              {joining ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Joining...
                </>
              ) : !token ? (
                'Log in to Join'
              ) : (
                `Join ${runNoun}`
              )}
            </Button>
          )}
          
          {/* Back link */}
          <div className="text-center mt-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-sm text-zinc-500 hover:text-zinc-700"
            >
              or browse other games
            </button>
          </div>
        </Card>
      </div>
      <Footer variant="slim" />
    </div>
  );
};

export default JoinGame;
